import { Canvas as CanvasType, FabricObject } from 'fabric';
import { getObjectName } from './fabricHelpers';

// Map to track which strokes belong to which optical choice
const opticalChoiceStrokes = new Map<string, string[]>();

/**
 * Associates a stroke with an optical choice
 * @param questionId - The question ID
 * @param choiceLabel - The choice label (A, B, C, D, E)
 * @param strokeId - The stroke/object ID to associate
 */
export const associateStrokeWithChoice = (
  questionId: string,
  choiceLabel: string,
  strokeId: string
) => {
  const key = `${questionId}_${choiceLabel}`;
  const strokes = opticalChoiceStrokes.get(key) || [];
  if (!strokes.includes(strokeId)) {
    strokes.push(strokeId);
    opticalChoiceStrokes.set(key, strokes);
  }
};

/**
 * Gets all stroke IDs associated with a choice
 */
export const getChoiceStrokes = (questionId: string, choiceLabel: string): string[] => {
  const key = `${questionId}_${choiceLabel}`;
  return opticalChoiceStrokes.get(key) || [];
};

/**
 * Removes all strokes associated with a choice from the canvas
 */
export const removeChoiceStrokes = (
  canvas: CanvasType,
  questionId: string,
  choiceLabel: string
) => {
  const key = `${questionId}_${choiceLabel}`;
  const strokeIds = opticalChoiceStrokes.get(key) || [];
  
  // Remove strokes from canvas
  const objects = canvas.getObjects();
  const objectsToRemove: FabricObject[] = [];
  
  objects.forEach(obj => {
    if (strokeIds.includes((obj as any).id || '')) {
      objectsToRemove.push(obj);
    }
  });
  
  objectsToRemove.forEach(obj => {
    canvas.remove(obj);
  });
  
  // Clear the association
  opticalChoiceStrokes.delete(key);
  
  canvas.requestRenderAll();
};

/**
 * Removes all strokes for a question
 */
export const removeQuestionStrokes = (canvas: CanvasType, questionId: string) => {
  const keysToRemove: string[] = [];
  
  // Find all keys for this question
  opticalChoiceStrokes.forEach((_, key) => {
    if (key.startsWith(`${questionId}_`)) {
      keysToRemove.push(key);
    }
  });
  
  // Remove strokes for each choice
  keysToRemove.forEach(key => {
    const [qId, choice] = key.split('_');
    removeChoiceStrokes(canvas, qId, choice);
  });
};

/**
 * Checks if a stroke is within the bounds of an optical choice
 */
export const isStrokeOnOpticalChoice = (
  canvas: CanvasType,
  strokeBounds: { left: number; top: number; width: number; height: number },
  questionId: string
): { choiceLabel: string; questionId: string } | null => {
  
  const choices = canvas.getObjects().filter(obj => {
    const name = getObjectName(obj);
    return name?.startsWith('opticalChoice_');
  });
  
  for (const choice of choices) {
    const choiceQuestionId = (choice as any).questionId;
    if (choiceQuestionId !== questionId) continue;
    
    const choiceBounds = choice.getBoundingRect();
    
    // Check if stroke overlaps with choice
    const overlaps = !(strokeBounds.left > choiceBounds.left + choiceBounds.width ||
                      strokeBounds.left + strokeBounds.width < choiceBounds.left ||
                      strokeBounds.top > choiceBounds.top + choiceBounds.height ||
                      strokeBounds.top + strokeBounds.height < choiceBounds.top);
    
    if (overlaps) {
      const choiceLabel = (choice as any).choiceLabel || getObjectName(choice)?.split('_')[1];
      return { choiceLabel, questionId };
    }
  }
  
  return null;
};

/**
 * Clears all optical choice stroke associations
 */
export const clearAllChoiceStrokes = () => {
  opticalChoiceStrokes.clear();
};

/**
 * Gets debug info about current associations
 */
export const getDebugInfo = () => {
  const info: Record<string, string[]> = {};
  opticalChoiceStrokes.forEach((strokes, key) => {
    info[key] = strokes;
  });
  return info;
};